import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/_shared/components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/_shared/components/ui/empty';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useOrpc } from '@/_shared/hooks/useOrpc';

interface GithubInstallationChooserProps {
  /** The `choose` token from `?github_choose=`. It names the installations this admin may connect. */
  choiceToken: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function isNotFound(error: unknown): boolean {
  return !!error && typeof error === 'object' && 'code' in error && error.code === 'NOT_FOUND';
}

/**
 * Lists the GitHub installations the admin's own GitHub sign-in can reach and this workspace has not
 * linked yet (ACT2-1076), and links the one they pick. The admin always picks, even from one candidate:
 * linking every reachable installation could expose another company's repositories to this workspace.
 */
export function GithubInstallationChooser({
  choiceToken,
  open,
  onOpenChange,
}: GithubInstallationChooserProps): React.ReactElement {
  const orpc = useOrpc();
  const queryClient = useQueryClient();

  const choicesQuery = useQuery(
    orpc.settings.scm.getInstallationChoices.queryOptions({ input: { choiceToken }, retry: false }),
  );

  const connect = useMutation(
    orpc.settings.scm.connectInstallation.mutationOptions({
      onSuccess: () => {
        toast.success('GitHub App connected');
        void queryClient.invalidateQueries({ queryKey: orpc.settings.scm.listRepos.key() });
        onOpenChange(false);
      },
      // The server spends the token on every attempt, so a retry from this dialog cannot succeed.
      onError: (err) => {
        toast.error(err.message || 'Failed to connect the GitHub installation');
        onOpenChange(false);
      },
    }),
  );

  const pendingId = connect.isPending ? connect.variables?.installationId : undefined;

  let body: React.ReactNode;
  if (choicesQuery.isPending) {
    body = (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full rounded-lg" />
        <Skeleton className="h-10 w-full rounded-lg" />
      </div>
    );
  } else if (choicesQuery.error) {
    body = isNotFound(choicesQuery.error) ? (
      <Callout variant="warning">This choice expired. Start again.</Callout>
    ) : (
      <Callout variant="error">{choicesQuery.error.message || 'Failed to load the GitHub installations.'}</Callout>
    );
  } else if (choicesQuery.data.choices.length === 0) {
    // The server answers no_new_installation instead of minting an empty choice, so this is a guard.
    body = (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No new GitHub installation to connect</EmptyTitle>
          <EmptyDescription>
            Your GitHub account must be able to see at least one repository the app is installed on.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  } else {
    body = (
      <ul
        aria-label="GitHub installations"
        className="flex flex-col divide-y divide-border rounded-lg border border-border"
      >
        {choicesQuery.data.choices.map((choice) => (
          <li key={choice.installationId} className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="min-w-0 truncate text-sm font-medium text-foreground">{choice.accountLogin}</span>
            <Button
              variant="primary"
              label="Connect"
              aria-label={`Connect ${choice.accountLogin}`}
              loading={pendingId === choice.installationId}
              disabled={connect.isPending}
              onClick={() => connect.mutate({ choiceToken, installationId: choice.installationId })}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !connect.isPending && onOpenChange(next)}>
      <DialogContent closeDisabled={connect.isPending}>
        <DialogHeader>
          <DialogTitle>Connect a GitHub installation</DialogTitle>
          <DialogDescription>
            Choose the GitHub account whose Lookout app installation to connect to this workspace.
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
